/**
 * HiraNova World — Canlı Chat Widget (polling tabanlı).
 *
 * E:\calculator chat modülünden uyarlandı. Anonim ziyaretçi (imzalı cookie).
 *  - Kapalı: yuvarlak balon + okunmamış rozeti (15sn poll).
 *  - Açık: aktif sohbet (composer) veya geçmiş listesi.
 *  - Yeni sohbette ad/soyad/telefon/e-posta zorunlu.
 *  - 3dk sessizlik → kapanış uyarısı, 30sn sonra otomatik kapanır.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './ChatWidget.css'

const POLL_OPEN = 3000
const POLL_CLOSED = 15000
const IDLE_WARN = 180000
const IDLE_GRACE = 30000

interface Msg { id: number; sender: string; body: string; created_at: string }
interface Conv { id: number; status: string; last_message: string; last_sender: string; last_message_at: string; visitor_unread: number; created_at: string }

const api = async (path: string, opts: { method?: string; body?: unknown } = {}) => {
  const res = await fetch(path, {
    method: opts.method || 'GET',
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
  })
  if (!res.ok) { const e = new Error('http') as Error & { status?: number }; e.status = res.status; throw e }
  return res.json()
}

const ChatWidget = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const lang = (i18n.language?.slice(0, 2) || 'tr')

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'chat' | 'list'>('chat')
  const [convId, setConvId] = useState<number | null>(null)
  const [convStatus, setConvStatus] = useState<string | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [history, setHistory] = useState<Conv[]>([])
  const [input, setInput] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [unread, setUnread] = useState(0)
  const [sending, setSending] = useState(false)
  const [idleLeft, setIdleLeft] = useState<number | null>(null)
  const [blocked, setBlocked] = useState(false)
  const [leadErr, setLeadErr] = useState('')

  const lastId = useRef(0)
  const timer = useRef<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const forceNew = useRef(false)
  const lastActivity = useRef(Date.now())

  const openRef = useRef(open); openRef.current = open
  const viewRef = useRef(view); viewRef.current = view
  const convIdRef = useRef(convId); convIdRef.current = convId

  const started = convId !== null || messages.length > 0
  const showLead = !started

  const merge = useCallback((incoming: Msg[]) => {
    if (!incoming?.length) return
    setMessages(prev => {
      const seen = new Set(prev.map(m => m.id))
      const fresh = incoming.filter(m => !seen.has(m.id))
      if (!fresh.length) return prev
      const next = [...prev, ...fresh]
      lastId.current = next.reduce((a, m) => Math.max(a, m.id), 0)
      return next
    })
  }, [])

  const poll = useCallback(async () => {
    try {
      if (!openRef.current) {
        const j = await api('/api/chat/list')
        setUnread(j.unread || 0)
        return
      }
      if (viewRef.current === 'list') {
        const j = await api('/api/chat/list')
        setHistory(j.conversations || [])
        setUnread(0)
        return
      }
      if (forceNew.current && !convIdRef.current) return
      const id = convIdRef.current
      const qs = `after=${lastId.current}&seen=1${id ? `&id=${id}` : ''}`
      const j = await api(`/api/chat/poll?${qs}`)
      if (j.conversationId && !convIdRef.current) { convIdRef.current = j.conversationId; setConvId(j.conversationId) }
      if (j.status) setConvStatus(j.status)
      const incoming: Msg[] = j.messages || []
      if (incoming.some(m => m.id > lastId.current && m.sender === 'admin')) lastActivity.current = Date.now()
      merge(incoming)
      setUnread(0)
    } catch { /* network blip */ }
  }, [merge])

  useEffect(() => {
    poll()
    if (timer.current) clearInterval(timer.current)
    timer.current = window.setInterval(poll, open ? POLL_OPEN : POLL_CLOSED)
    return () => { if (timer.current) clearInterval(timer.current) }
  }, [open, view, poll])

  useEffect(() => {
    if (!(open && view === 'chat' && convStatus === 'open' && convId)) { setIdleLeft(null); return }
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - lastActivity.current
      if (elapsed < IDLE_WARN) { setIdleLeft(null); return }
      const left = Math.ceil((IDLE_WARN + IDLE_GRACE - elapsed) / 1000)
      if (left <= 0) { setIdleLeft(null); autoClose() } else setIdleLeft(left)
    }, 1000)
    return () => clearInterval(tick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, convStatus, convId])

  useEffect(() => {
    if (open && view === 'chat' && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, open, view])

  const autoClose = async () => {
    const id = convIdRef.current
    if (!id) return
    try { await api('/api/chat/close', { method: 'POST', body: { conversationId: id } }) } catch { /**/ }
    setConvStatus('closed')
  }
  const stayOpen = () => { lastActivity.current = Date.now(); setIdleLeft(null) }
  const endConversation = async () => {
    const id = convIdRef.current
    if (!id) return
    setIdleLeft(null)
    try { await api('/api/chat/close', { method: 'POST', body: { conversationId: id } }) } catch { /**/ }
    setConvStatus('closed')
  }
  const startNew = () => {
    forceNew.current = true; lastActivity.current = Date.now(); lastId.current = 0
    convIdRef.current = null; setConvId(null); setConvStatus(null); setMessages([])
    setIdleLeft(null); setBlocked(false); setLeadErr('')
    viewRef.current = 'chat'; setView('chat')
  }
  const showHistory = () => { viewRef.current = 'list'; setView('list') }
  const openConversation = (c: Conv) => {
    forceNew.current = false; lastActivity.current = Date.now(); lastId.current = 0
    convIdRef.current = c.id; setConvId(c.id); setConvStatus(c.status); setMessages([])
    setIdleLeft(null); viewRef.current = 'chat'; setView('chat'); poll()
  }

  const send = async () => {
    const body = input.trim()
    if (!body || sending) return
    if (showLead) {
      if (!firstName.trim() || !lastName.trim() || !phone.trim() || !email.trim()) { setLeadErr(t('chat.requiredFields')); return }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setLeadErr(t('chat.invalidEmail')); return }
    }
    setLeadErr(''); setSending(true); lastActivity.current = Date.now(); setIdleLeft(null)
    const optimistic: Msg = { id: -Date.now(), sender: 'visitor', body, created_at: new Date().toISOString() }
    setMessages(prev => [...prev, optimistic])
    setInput('')
    try {
      const j = await api('/api/chat/send', {
        method: 'POST',
        body: {
          body,
          name: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
          email: email.trim(), phone: phone.trim(), lang,
          conversationId: convIdRef.current || undefined,
          newSession: forceNew.current || undefined,
        },
      })
      forceNew.current = false
      if (j.conversationId) { convIdRef.current = j.conversationId; setConvId(j.conversationId) }
      setConvStatus('open')
      setMessages(prev => {
        const without = prev.filter(m => m.id !== optimistic.id)
        const next = j.message ? [...without, j.message] : without
        lastId.current = next.reduce((a, m) => Math.max(a, m.id), lastId.current)
        return next
      })
    } catch (e) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
      if ((e as { status?: number })?.status === 403) setBlocked(true)
      else setInput(body)
    } finally { setSending(false) }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }
  const fmtTime = (iso: string) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch { return '' } }
  const fmtDate = (iso: string) => { try { return new Date(iso).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) } catch { return '' } }

  // Detay sayfalarında (mobile rezervasyon bar'ı olan) ve genel planda gizleme yok; her yerde görünür.
  if (location.pathname.includes('/admin')) return null

  const isClosed = convStatus === 'closed'

  return (
    <div className="hnw-chat">
      {open && (
        <div className="hnw-chat-panel">
          {/* Header */}
          <div className="hnw-chat-header">
            <div className="hnw-chat-title">
              <span className="hnw-chat-dot" />
              <div>
                <strong>HiraNova Family Club</strong>
                <span className="hnw-chat-sub">{t('chat.support')}</span>
              </div>
            </div>
            <div className="hnw-chat-head-actions">
              {view === 'chat' && <button onClick={showHistory} title={t('chat.history')} aria-label={t('chat.history')}>☰</button>}
              {view === 'list' && <button onClick={() => setView('chat')} title={t('chat.back')} aria-label={t('chat.back')}>←</button>}
              <button onClick={() => setOpen(false)} title={t('chat.close')} aria-label={t('chat.close')}>×</button>
            </div>
          </div>

          {view === 'list' ? (
            <div className="hnw-chat-list">
              <button className="hnw-chat-new" onClick={startNew}>+ {t('chat.newChat')}</button>
              {history.length === 0 ? (
                <p className="hnw-chat-empty">{t('chat.noChats')}</p>
              ) : history.map(c => (
                <button key={c.id} className="hnw-chat-list-item" onClick={() => openConversation(c)}>
                  <div className="hnw-chat-list-top">
                    <span>{c.status === 'open' ? `● ${t('chat.open')}` : t('chat.closed')}</span>
                    <span className="hnw-chat-list-time">{fmtDate(c.last_message_at)}</span>
                  </div>
                  <div className="hnw-chat-list-msg">{c.last_sender === 'admin' ? 'HiraNova Family Club: ' : ''}{c.last_message || '—'}</div>
                  {c.visitor_unread > 0 && <span className="hnw-chat-list-badge">{c.visitor_unread}</span>}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="hnw-chat-body" ref={scrollRef}>
                {showLead && (
                  <div className="hnw-chat-lead">
                    <p className="hnw-chat-lead-intro">{t('chat.leadIntro')}</p>
                    <div className="hnw-chat-lead-row">
                      <input placeholder={t('chat.firstName')} value={firstName} onChange={e => setFirstName(e.target.value)} />
                      <input placeholder={t('chat.lastName')} value={lastName} onChange={e => setLastName(e.target.value)} />
                    </div>
                    <input placeholder={t('chat.phone')} value={phone} onChange={e => setPhone(e.target.value)} />
                    <input placeholder={t('chat.email')} type="email" value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                )}

                {messages.map(m => (
                  <div key={m.id} className={`hnw-msg ${m.sender === 'admin' ? 'admin' : 'visitor'}`}>
                    <div className="hnw-msg-bubble">{m.body}</div>
                    <div className="hnw-msg-time">{fmtTime(m.created_at)}</div>
                  </div>
                ))}

                {idleLeft != null && (
                  <div className="hnw-chat-idle">
                    {t('chat.idleClose', { sec: idleLeft })}
                    <button onClick={stayOpen}>{t('chat.stayOpen')}</button>
                  </div>
                )}
                {isClosed && (
                  <div className="hnw-chat-closed">
                    {t('chat.convClosed')}
                    <button onClick={startNew}>{t('chat.startNew')}</button>
                  </div>
                )}
                {blocked && <div className="hnw-chat-blocked">{t('chat.cannotSend')}</div>}
              </div>

              {leadErr && <div className="hnw-chat-err">{leadErr}</div>}

              {!isClosed && !blocked && (
                <div className="hnw-chat-composer">
                  <textarea
                    rows={1}
                    placeholder={t('chat.inputPlaceholder')}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                  <button onClick={send} disabled={sending || !input.trim()} aria-label={t('chat.send')}>➤</button>
                </div>
              )}
              {convStatus === 'open' && convId && (
                <button className="hnw-chat-end" onClick={endConversation}>{t('chat.endChat')}</button>
              )}
            </>
          )}
        </div>
      )}

      <button className="hnw-chat-bubble" onClick={() => setOpen(o => !o)} aria-label={t('chat.support')}>
        {open ? (
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            <circle cx="8.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
            <circle cx="12" cy="11.5" r="1" fill="currentColor" stroke="none" />
            <circle cx="15.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
          </svg>
        )}
        {!open && unread > 0 && <span className="hnw-chat-bubble-badge">{unread}</span>}
      </button>
    </div>
  )
}

export default ChatWidget
